import React, { useState } from "react";
import { api, formatApiError, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import QuoteReplyModal from "./QuoteReplyModal";

const STATUS_OPTIONS = ["open", "contacted", "accepted", "declined", "expired"];
const STATUS_TONE = {
  open: "bg-muted text-ink",
  contacted: "bg-ink text-white",
  accepted: "bg-leaf/15 text-leaf border-leaf/40",
  declined: "bg-danger/15 text-danger border-danger/40",
  expired: "bg-danger/15 text-danger border-danger/40",
};

/**
 * Detail dialog for a single quote_requests record.
 *
 * Two reply paths:
 *  1) "Reply in app" — opens the existing QuoteReplyModal (Resend-powered,
 *     audit-logged, marks the quote as `contacted`).
 *  2) "Open in mail client" — generates a mailto: URL pre-filled with the
 *     contact email, the GreenBelt subject line, and a polite body that includes
 *     the full quote context. Works with Outlook, Gmail, Apple Mail, Thunderbird.
 */
export default function QuoteDetailDialog({ quote, onClose, onChanged }) {
  const [q, setQ] = useState(quote);
  const [showReply, setShowReply] = useState(false);
  const [busy, setBusy] = useState(false);

  const changeStatus = async (status) => {
    if (status === q.status) return;
    setBusy(true);
    try {
      await api.patch(`/admin/quotes/${q.ref}`, null, { params: { status } });
      setQ((prev) => ({ ...prev, status }));
      toast.success(`Marked ${q.ref} as ${status}`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const mailtoHref = buildMailto(q);

  return (
    <>
      <div
        className="fixed inset-0 z-[55] flex items-center justify-center px-4 py-8 bg-ink/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="quote-detail-overlay"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl bg-bg border border-ink shadow-2xl max-h-[92vh] overflow-y-auto"
          data-testid="quote-detail-dialog"
        >
          <div className="border-b border-line px-6 py-4 sticky top-0 bg-bg flex items-start justify-between">
            <div>
              <div className="label-tag">Quote request</div>
              <div className="font-serif text-2xl mt-1" data-testid="quote-ref">{q.ref}</div>
              <div className="text-[11px] font-mono text-ink2 mt-1">
                {q.company_name} · {fmtMoney(Number(q.total_usd || 0), "USD")} · {Number(q.tons || 0).toLocaleString()} tCO₂
              </div>
            </div>
            <button onClick={onClose} aria-label="Close"
              data-testid="quote-detail-close"
              className="text-ink2 hover:text-ink text-2xl leading-none px-2">×</button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Status chip row */}
            <div>
              <div className="label-tag mb-2">Status</div>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => {
                  const active = s === q.status;
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={busy}
                      onClick={() => changeStatus(s)}
                      data-testid={`quote-status-set-${s}`}
                      className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                        active ? (STATUS_TONE[s] || "bg-ink text-white") : "border-line text-ink2 hover:border-ink hover:text-ink"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Two-column grid of metadata */}
            <div className="border border-line">
              <KV k="Submitted" v={fmtDate(q.created_at)} />
              <KV k="Tier" v={q.tier} />
              <KV k="Company" v={q.company_name} />
              <KV k="Contact" v={q.contact_name} />
              <KV k="Email" v={q.contact_email} mono />
              <KV k="Phone" v={q.contact_phone} mono />
              <KV k="Project" v={q.project_name || q.project_id || "—"} mono />
              <KV k="Tons" v={Number(q.tons || 0).toLocaleString()} />
              <KV k="Total" v={fmtMoney(Number(q.total_usd || 0), q.currency || "USD")} />
              <KV k="Currency" v={q.currency} />
              <KV k="Settlement" v={q.settlement_method} />
            </div>

            {q.notes && (
              <div>
                <div className="label-tag mb-1">Buyer notes</div>
                <div className="border border-line p-3 text-sm text-ink whitespace-pre-wrap bg-surface">{q.notes}</div>
              </div>
            )}

            {q.reply && (
              <div className="border border-leaf/40 bg-leaf/5 p-4">
                <div className="label-tag mb-1 text-leaf">Last reply sent</div>
                <div className="text-sm grid grid-cols-2 gap-x-4 gap-y-1 mt-2 font-mono">
                  <span className="text-ink2">When:</span><span>{fmtDate(q.reply.sent_at)}</span>
                  <span className="text-ink2">Price / ton:</span><span>${Number(q.reply.price_per_ton_usd).toFixed(2)}</span>
                  <span className="text-ink2">Total:</span><span>${Number(q.reply.total_usd).toLocaleString()}</span>
                  <span className="text-ink2">Valid until:</span><span>{q.reply.expiry_date}</span>
                  <span className="text-ink2">Rail:</span><span>{q.reply.payment_rail}</span>
                </div>
                {q.reply.notes && <div className="text-xs text-ink2 mt-2 italic">"{q.reply.notes}"</div>}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="border-t border-line px-6 py-3 sticky bottom-0 bg-bg flex flex-wrap justify-end gap-2">
            <a
              href={mailtoHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-mailto"
              onClick={() => toast.success(`Opening mail client for ${q.contact_email}`)}
              className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-line text-ink hover:bg-ink hover:text-white"
              title="Open in your default email client (Outlook, Gmail, Apple Mail…)"
            >
              ✉ Reply via mail client
            </a>
            <button
              type="button"
              onClick={() => setShowReply(true)}
              data-testid="quote-reply-app"
              className="btn-primary !text-xs !px-5 !py-2"
            >
              {q.reply ? "↗ Resend structured reply" : "↗ Reply in app (Resend)"}
            </button>
          </div>
        </div>
      </div>

      {showReply && (
        <QuoteReplyModal
          quote={q}
          onClose={() => setShowReply(false)}
          onSent={() => { setShowReply(false); onChanged?.(); onClose(); }}
        />
      )}
    </>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="grid grid-cols-3 border-b border-line/60 last:border-b-0">
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-ink2 bg-surface">{k}</div>
      <div className={`px-4 py-2 col-span-2 text-sm text-ink ${mono ? "font-mono break-all" : ""}`}>{v || "—"}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function buildMailto(q) {
  const to = encodeURIComponent(q.contact_email || "");
  const subj = encodeURIComponent(`GreenBelt quote ${q.ref} · ${q.company_name || ""}`);
  const lines = [
    `Hello ${q.contact_name || "there"},`,
    "",
    `Thank you for your interest in GreenBelt's Verified Carbon Credit Sustainability units.`,
    "",
    `Quote reference : ${q.ref}`,
    `Company         : ${q.company_name || "—"}`,
    `Project         : ${q.project_name || q.project_id || "—"}`,
    `Tons            : ${Number(q.tons || 0).toLocaleString()} tCO₂`,
    `Requested total : ${fmtMoney(Number(q.total_usd || 0), q.currency || "USD")}`,
    `Settlement      : ${q.settlement_method || "—"}`,
    "",
    `Our trading desk has prepared the following indicative offer:`,
    "",
    `  • Price per tCO₂ : USD <enter price>`,
    `  • Total          : USD <enter total>`,
    `  • Valid until    : <enter date>`,
    `  • Settlement     : ${q.settlement_method || "SWIFT / USDT"}`,
    "",
    "Please reply confirming acceptance and we will issue the order package with bank details and the KYB acceptance form.",
    "",
    "Kind regards,",
    "GreenBelt Trading Desk",
    "trading@greenbelt.io",
  ];
  const body = encodeURIComponent(lines.join("\n"));
  return `mailto:${to}?subject=${subj}&body=${body}`;
}
